import { useCallback, useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  Paginated,
  PostRow,
  ReactionEmoji,
  ThreadCategory,
  ThreadDetail,
  ThreadRow,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// @-mention autocomplete — flat org-wide active user list, server filters
// by ?q= (substring on first/last name + email). Returns up to 20 rows.
// Distinct from useUsers, which is dept-scoped for non-admin actors.
// ---------------------------------------------------------------------------

export interface MentionableUser {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

export function useMentionableUsers(query: string) {
  return useQuery<MentionableUser[]>({
    queryKey: ["users", "mentionable", query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      const { data } = await api.get<MentionableUser[]>(
        `/users/mentionable/?${params.toString()}`,
      );
      return data;
    },
    // Cheap server query; cache by exact `q` so retyping the same prefix
    // doesn't re-fetch. The popup re-mounts often, so a short stale time
    // is fine.
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Threads — list / retrieve / create / mod actions
// ---------------------------------------------------------------------------

interface ThreadsQuery {
  search?: string;
  category?: ThreadCategory | "";
  page?: number;
}

export function useThreads(q: ThreadsQuery = {}) {
  return useQuery<Paginated<ThreadRow>>({
    queryKey: ["threads", q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q.search)   params.set("search",   q.search);
      if (q.category) params.set("category", q.category);
      if (q.page)     params.set("page",     String(q.page));
      const { data } = await api.get<Paginated<ThreadRow>>(
        `/threads/?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useThread(id: string | undefined) {
  return useQuery<ThreadDetail>({
    enabled: id != null && !Number.isNaN(id),
    queryKey: ["threads", "detail", id],
    queryFn: async () => {
      const { data } = await api.get<ThreadDetail>(`/threads/${id}/`);
      return data;
    },
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; category: ThreadCategory; body?: string }) => {
      const { data } = await api.post<ThreadDetail>("/threads/", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["threads"] }),
  });
}

export function usePinThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<ThreadDetail>(`/threads/${id}/pin/`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["threads"] }),
  });
}

export function useLockThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<ThreadDetail>(`/threads/${id}/lock/`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["threads"] }),
  });
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/threads/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["threads"] }),
  });
}

export function useSubscribeThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<ThreadDetail>(`/threads/${id}/subscribe/`);
      return data;
    },
    onSuccess: (_t, id) => {
      qc.invalidateQueries({ queryKey: ["threads", "detail", id] });
      qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}

export function useUnsubscribeThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<ThreadDetail>(`/threads/${id}/unsubscribe/`);
      return data;
    },
    onSuccess: (_t, id) => {
      qc.invalidateQueries({ queryKey: ["threads", "detail", id] });
      qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Posts — chronological list for one thread + create / edit / hide
// ---------------------------------------------------------------------------

export function usePosts(threadId: string | undefined) {
  return useQuery<Paginated<PostRow>>({
    enabled: threadId != null && !Number.isNaN(threadId),
    queryKey: ["posts", threadId],
    queryFn: async () => {
      const { data } = await api.get<Paginated<PostRow>>(
        `/posts/?thread=${threadId}`,
      );
      return data;
    },
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      thread: string;
      content: string;
      files?: File[];
      reply_to?: string | null;
      /** Callback fired during multipart upload only. Called with a
       *  percent 0–100 (or -1 when the browser can't compute total —
       *  rare, but match UploadProgressBar's "indeterminate" sentinel).
       *  Ignored for plain JSON posts. */
      onUploadProgress?: (percent: number) => void;
    }) => {
      // Multipart only when there are files; plain JSON otherwise so the
      // CSRF/JSON path stays the common case.
      if (input.files && input.files.length > 0) {
        const fd = new FormData();
        fd.append("thread", String(input.thread));
        fd.append("content", input.content);
        if (input.reply_to != null) fd.append("reply_to", String(input.reply_to));
        for (const f of input.files) fd.append("attachments", f);
        const { data } = await api.post<PostRow>("/posts/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!input.onUploadProgress) return;
            const total = evt.total ?? 0;
            if (!total) {
              // Some browsers can't read Content-Length on a streamed
              // multipart body → tell the UI to render the
              // indeterminate state.
              input.onUploadProgress(-1);
              return;
            }
            input.onUploadProgress(Math.round((evt.loaded / total) * 100));
          },
        });
        return data;
      }
      const body: Record<string, unknown> = {
        thread: input.thread,
        content: input.content,
      };
      if (input.reply_to != null) body.reply_to = input.reply_to;
      const { data } = await api.post<PostRow>("/posts/", body);
      return data;
    },
    onSuccess: (_post, vars) => {
      qc.invalidateQueries({ queryKey: ["posts", vars.thread] });
      qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}

/** Curated emoji catalog (groups + emojis), mirrors the backend's
 *  PostReaction.EMOJI_GROUPS. Long staleTime since the set rarely
 *  changes — and even if it does, a page reload picks up the new
 *  catalog. The hook is what the reaction picker reads from instead
 *  of a hardcoded list in the component, so we don't have to keep
 *  two places in sync. */
export interface EmojiGroup { name: string; emojis: string[] }

export function useReactionsCatalog() {
  return useQuery<{ groups: EmojiGroup[] }>({
    queryKey: ["reactions", "catalog"],
    queryFn: async () => {
      const { data } = await api.get<{ groups: EmojiGroup[] }>(
        "/reactions/catalog/",
      );
      return data;
    },
    staleTime: 60 * 60_000,  // 1 hour — catalog is essentially static.
  });
}

export function useReactToPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { postId: string; thread: string; emoji: ReactionEmoji }) => {
      const { data } = await api.post<PostRow>(`/posts/${input.postId}/react/`, {
        emoji: input.emoji,
      });
      return data;
    },
    onSuccess: (_p, vars) => qc.invalidateQueries({ queryKey: ["posts", vars.thread] }),
  });
}

export function useUpdatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; thread: string; content: string }) => {
      const { data } = await api.patch<PostRow>(`/posts/${input.id}/`, {
        content: input.content,
      });
      return data;
    },
    onSuccess: (_p, vars) => qc.invalidateQueries({ queryKey: ["posts", vars.thread] }),
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; thread: string }) => {
      await api.delete(`/posts/${input.id}/`);
    },
    onSuccess: (_x, vars) => qc.invalidateQueries({ queryKey: ["posts", vars.thread] }),
  });
}

// ---------------------------------------------------------------------------
// WebSocket — apps.private.consumers.ThreadConsumer pushes new posts.
// Like the notification socket, this is wake-up-only: every message
// invalidates the cached post list so REST stays the source of truth.
// ---------------------------------------------------------------------------

/** Each `typing.start` from another user lands here. Auto-expires after
 *  3 s of inactivity (or sooner if a `typing.stop` arrives) so a tab
 *  that closes mid-type doesn't leave a ghost chip behind forever. */
export interface TypingUser { user_id: string; name: string; expiresAt: number }

/** Return signature of useThreadSocket — gives the ReplyComposer a
 *  `sendTyping(true|false)` it can throttle, and a live list of OTHER
 *  users typing in this thread to render below the input. */
export interface ThreadSocketHandle {
  /** Tell the server I'm typing (true) or stopped (false). Throttling
   *  is up to the caller — the consumer accepts every ping. */
  sendTyping: (typing: boolean) => void;
  /** Currently-typing users other than me; updates live. */
  typingUsers: TypingUser[];
}

export function useThreadSocket(threadId: string | undefined): ThreadSocketHandle {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);

  // Periodically prune entries whose 3-second TTL has elapsed without a
  // refresh — covers the "tab closed mid-type" ghost case.
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => prev.filter((u) => u.expiresAt > now));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!threadId) return;
    if (typeof window === "undefined") return;

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/ws/forum/thread/${threadId}/`;

    let alive = true;
    let retry = 0;

    function connect() {
      if (!alive) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen    = () => { retry = 0; };
      ws.onmessage = (evt) => {
        // The thread WS now multiplexes `post.new` / `post.deleted` and
        // `typing.start` / `typing.stop`. Cache invalidation only fires
        // for the post events; typing is a UI-only signal.
        try {
          const data = JSON.parse(evt.data as string) as
            | { type: "post.new" | "post.deleted" }
            | { type: "typing.start"; user_id: string; name: string }
            | { type: "typing.stop";  user_id: string };
          if (data.type === "post.new" || data.type === "post.deleted") {
            qc.invalidateQueries({ queryKey: ["posts", threadId] });
            return;
          }
          if (data.type === "typing.start") {
            const expiresAt = Date.now() + 3_000;
            setTypingUsers((prev) => {
              // De-dupe by user_id; refresh expiry if already present.
              const without = prev.filter((u) => u.user_id !== data.user_id);
              return [...without, { user_id: data.user_id, name: data.name, expiresAt }];
            });
            return;
          }
          if (data.type === "typing.stop") {
            setTypingUsers((prev) => prev.filter((u) => u.user_id !== data.user_id));
            return;
          }
        } catch {
          // Fall back to the old behaviour on any malformed frame.
          qc.invalidateQueries({ queryKey: ["posts", threadId] });
        }
      };
      ws.onclose   = () => {
        wsRef.current = null;
        if (!alive) return;
        retry += 1;
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(retry, 5));
        setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    }
    connect();
    return () => {
      alive = false;
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [threadId, qc]);

  const sendTyping = useCallback((typing: boolean) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ action: "typing", typing }));
    } catch { /* noop — best effort */ }
  }, []);

  return { sendTyping, typingUsers };
}
